import { useEffect, useState } from 'react'
import api from '@/services/api'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { UserListTable, UserActionModal } from '@/components/User'
import type { ColumnConfig, FilterConfig, PaginationState, UserAction, Singer } from '@/components/User'
import PageHeader from '@/components/PageHeader'
import ErrorDisplay from '@/components/ErrorDisplay'

/**
 * Singers Management Page (Using reusable components)
 * Dark mode geek aesthetic - Desktop only
 */
export default function SingersPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [singers, setSingers] = useState<Singer[]>([])
  const [filteredSingers, setFilteredSingers] = useState<Singer[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Pagination state
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    pageSize: 25,
    total: 0,
  })

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'suspended'>('all')

  // Modal state
  const [modalState, setModalState] = useState<{
    isOpen: boolean
    action: UserAction | null
    selectedSinger: Singer | null
    isLoading: boolean
  }>({
    isOpen: false,
    action: null,
    selectedSinger: null,
    isLoading: false,
  })

  // Fetch singers on mount
  useEffect(() => {
    const fetchSingers = async () => {
      try {
        const response = await api.get('/superadmin/users?role=singer')
        if (response.data.success) {
          // Handle both array and nested data structure
          const data = Array.isArray(response.data.data) ? response.data.data : response.data.data?.users || []
          setSingers(data)
          setPagination((prev) => ({
            ...prev,
            total: data.length,
          }))
        }
      } catch (err: any) {
        setError(err.response?.data?.error || t('common.error'))
      } finally {
        setLoading(false)
      }
    }

    fetchSingers()
  }, [t])

  // Filter singers based on search and status
  useEffect(() => {
    let filtered = singers

    // Apply search filter
    if (searchTerm) {
      const lower = searchTerm.toLowerCase()
      filtered = filtered.filter(
        (singer) =>
          (singer.email?.toLowerCase().includes(lower) || false) ||
          (singer.profile?.firstName?.toLowerCase().includes(lower) || false) ||
          (singer.profile?.lastName?.toLowerCase().includes(lower) || false) ||
          (singer.profile?.pseudo?.toLowerCase().includes(lower) || false)
      )
    }

    // Apply status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter((singer) => !singer.isSuspended)
    } else if (statusFilter === 'suspended') {
      filtered = filtered.filter((singer) => singer.isSuspended)
    }

    setFilteredSingers(filtered)
    setPagination((prev) => ({
      ...prev,
      total: filtered.length,
      page: 1, // Reset to first page on filter change
    }))
  }, [singers, searchTerm, statusFilter])

  // Get paginated data
  const paginatedSingers = filteredSingers.slice(
    (pagination.page - 1) * pagination.pageSize,
    pagination.page * pagination.pageSize
  )

  // Handle row click (navigate to details)
  const handleRowClick = (singer: any) => {
    navigate(`/singers/${singer.id}`)
  }

  // Handle action modal open
  const handleAction = (singerId: string, action: UserAction) => {
    const singer = singers.find((s) => s.id === singerId)
    if (!singer) return

    // Guest singers can't be suspended, unsuspended, or have password reset
    if (singer.isGuest && (action === 'suspend' || action === 'unsuspend' || action === 'reset-password')) {
      return // Silently ignore these actions for guests
    }

    setModalState({
      isOpen: true,
      action,
      selectedSinger: singer,
      isLoading: false,
    })
  }

  // Handle action confirm
  const handleConfirmAction = async () => {
    if (!modalState.selectedSinger || !modalState.action) return

    setModalState((prev) => ({ ...prev, isLoading: true }))

    try {
      const singerId = modalState.selectedSinger.id

      // Guest singers can only be deleted, not suspended/unsuspended/reset-password
      if (modalState.selectedSinger.isGuest && modalState.action !== 'delete') {
        setError('Cette action n\'est pas disponible pour les chanteurs invités')
        setModalState((prev) => ({ ...prev, isLoading: false }))
        return
      }

      switch (modalState.action) {
        case 'suspend':
          await api.put(`/superadmin/users/${singerId}/status`, { status: 'suspended' })
          setSingers((prev) =>
            prev.map((singer) =>
              singer.id === singerId ? { ...singer, isSuspended: true } : singer
            )
          )
          break

        case 'unsuspend':
          await api.put(`/superadmin/users/${singerId}/status`, { status: 'active' })
          setSingers((prev) =>
            prev.map((singer) =>
              singer.id === singerId ? { ...singer, isSuspended: false } : singer
            )
          )
          break

        case 'delete':
          // For guest singers, we can't delete via the user endpoint
          // They would need to be deleted from the Singer collection directly
          // For now, we'll just remove them from the list
          if (modalState.selectedSinger.isGuest) {
            setSingers((prev) => prev.filter((singer) => singer.id !== singerId))
          } else {
            await api.delete(`/superadmin/users/${singerId}`)
            setSingers((prev) => prev.filter((singer) => singer.id !== singerId))
          }
          break

        case 'reset-password':
          await api.post(`/superadmin/users/${singerId}/reset-password`)
          break

        default:
          break
      }

      setModalState({
        isOpen: false,
        action: null,
        selectedSinger: null,
        isLoading: false,
      })
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.error'))
      setModalState((prev) => ({ ...prev, isLoading: false }))
    }
  }

  // Column configuration
  const columns: ColumnConfig[] = [
    {
      key: 'profile.firstName',
      label: t('common.name'),
      sortable: true,
      render: (_, singer) => {
        if (singer.isGuest) {
          return (
            <span>
              {singer.profile?.pseudo || '—'} <span className="text-xs text-slate-400">(Guest)</span>
            </span>
          )
        }
        return `${singer.profile?.firstName || '—'} ${singer.profile?.lastName || '—'}`
      },
    },
    {
      key: 'email',
      label: t('common.email'),
      sortable: true,
      render: (value, singer) => {
        if (singer.isGuest) {
          return <span className="text-slate-400 italic">Guest (pas de compte)</span>
        }
        return value || '—'
      },
    },
    {
      key: 'pseudo',
      label: 'Pseudo',
      render: (_, singer) => singer.profile?.pseudo || '—',
    },
    {
      key: 'eventsJoined',
      label: t('superadmin.eventsJoined'),
      sortable: true,
      render: (value, singer) => singer.eventsCount || 0,
    },
    {
      key: 'isSuspended',
      label: t('common.status'),
      render: (value) => (
        <span
          className={`px-3 py-1 rounded-full text-xs font-bold ${
            value
              ? 'bg-red-900/50 text-red-300 border border-red-700/50'
              : 'bg-green-900/50 text-green-300 border border-green-700/50'
          }`}
        >
          {value ? t('common.suspended') : t('common.active')}
        </span>
      ),
    },
    {
      key: 'createdAt',
      label: t('common.joinedDate'),
      sortable: true,
      render: (value) => new Date(value).toLocaleDateString('fr-FR'),
    },
  ]

  // Filter configuration
  const filters: FilterConfig[] = [
    {
      key: 'status',
      label: t('common.status'),
      type: 'select',
      options: [
        { value: 'all', label: t('common.all') },
        { value: 'active', label: t('common.active') },
        { value: 'suspended', label: t('common.suspended') },
      ],
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('superadmin.singers')}
        subtitle="list"
        count={filteredSingers.length}
        subtitleColor="pink"
        titleGradient="from-cyan-400 to-pink-400"
      />

      <ErrorDisplay error={error} />

      {/* User List Table */}
      <UserListTable
        users={paginatedSingers}
        columns={columns}
        loading={loading}
        error={error}
        searchPlaceholder="Email, nom, pseudo..."
        filters={filters}
        pagination={pagination}
        onSearch={(term) => setSearchTerm(term)}
        onFilterChange={(filters) => {
          setStatusFilter((filters.status as 'all' | 'active' | 'suspended') || 'all')
        }}
        onPaginationChange={(page, pageSize) => {
          setPagination((prev) => ({
            ...prev,
            page,
            pageSize,
          }))
        }}
        onRowClick={handleRowClick}
        onAction={handleAction}
        userType="singer"
        emptyMessage="Aucun chanteur trouvé"
        getRowClassName={(singer) => {
          return 'hover:bg-slate-800/50 transition group cursor-pointer'
        }}
      />

      {/* Action Modal */}
      <UserActionModal
        isOpen={modalState.isOpen}
        action={modalState.action}
        userType="singer"
        userName={
          modalState.selectedSinger
            ? `${modalState.selectedSinger.profile?.firstName} ${modalState.selectedSinger.profile?.lastName}`
            : ''
        }
        isLoading={modalState.isLoading}
        onConfirm={handleConfirmAction}
        onCancel={() =>
          setModalState({
            isOpen: false,
            action: null,
            selectedSinger: null,
            isLoading: false,
          })
        }
      />
    </div>
  )
}
